// SupplierMaster.js — CRUD page for the supplier/vendor master. Hooks into
// the new backend endpoints (/Home/AddSupplier, /Home/UpdateSupplier,
// /Home/DeleteSupplier, /Home/GetSuppliers, /Home/GetSupplierOutstanding).
// StorePurchase + reports both read from this list, so creating a supplier
// here makes it pickable everywhere automatically.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VisibilityIcon from '@mui/icons-material/Visibility';
import config from '../../config';

const EMPTY_FORM = {
  id: 0,
  supplierName: '',
  mobileNumber: '',
  email: '',
  gstNumber: '',
  address: '',
  companyName: '',
  contactPerson: '',
  activeStatus: true,
};

export default function SupplierMaster() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');
  const Companycurrency = company?.currency ?? '';

  const [suppliers, setSuppliers] = useState([]);
  const [outstanding, setOutstanding] = useState([]); // SupplierOutstandingDto[]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const [outstandingDialogOpen, setOutstandingDialogOpen] = useState(false);
  const [outstandingDetail, setOutstandingDetail] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ─── Data loaders ─────────────────────────────────────────────────────
  const fetchSuppliers = async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const [supRes, outRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/Home/GetSuppliers/${company.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(
          `${config.API_BASE_URL}/Home/GetSupplierOutstanding/${company.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ]);
      setSuppliers(supRes.data || []);
      setOutstanding(outRes.data || []);
    } catch (err) {
      console.error('Supplier load failed:', err);
      notify('Failed to load suppliers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const outstandingMap = useMemo(() => {
    const m = {};
    outstanding.forEach((o) => {
      m[o.supplierId] = o;
    });
    return m;
  }, [outstanding]);

  // ─── Filtering ────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return suppliers.filter((s) => {
      if (statusFilter === 'active' && !s.activeStatus) return false;
      if (statusFilter === 'inactive' && s.activeStatus) return false;
      if (!q) return true;
      return (
        (s.supplierName || '').toLowerCase().includes(q) ||
        (s.mobileNumber || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q) ||
        (s.gstNumber || '').toLowerCase().includes(q) ||
        (s.companyName || '').toLowerCase().includes(q) ||
        (s.contactPerson || '').toLowerCase().includes(q)
      );
    });
  }, [suppliers, search, statusFilter]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // ─── CRUD handlers ────────────────────────────────────────────────────
  const openAdd = () => {
    setForm({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEdit = (supplier) => {
    setForm({
      id: supplier.id,
      supplierName: supplier.supplierName || '',
      mobileNumber: supplier.mobileNumber || '',
      email: supplier.email || '',
      gstNumber: supplier.gstNumber || '',
      address: supplier.address || '',
      companyName: supplier.companyName || '',
      contactPerson: supplier.contactPerson || '',
      activeStatus: supplier.activeStatus !== false,
    });
    setDialogOpen(true);
  };

  const saveSupplier = async () => {
    if (!form.supplierName.trim()) {
      notify('Supplier name is required', 'warning');
      return;
    }
    if (form.mobileNumber && !/^[0-9+\-\s()]{6,15}$/.test(form.mobileNumber)) {
      notify('Mobile number format looks invalid', 'warning');
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      notify('Email format looks invalid', 'warning');
      return;
    }

    setSaving(true);
    try {
      const isEdit = form.id > 0;
      const payload = {
        ...(isEdit ? { id: form.id } : {}),
        companyId: company?.id,
        supplierName: form.supplierName.trim(),
        mobileNumber: form.mobileNumber.trim(),
        email: form.email.trim(),
        gstNumber: form.gstNumber.trim(),
        address: form.address.trim(),
        companyName: form.companyName.trim(),
        contactPerson: form.contactPerson.trim(),
        activeStatus: form.activeStatus,
      };
      const url = isEdit
        ? `${config.API_BASE_URL}/Home/UpdateSupplier`
        : `${config.API_BASE_URL}/Home/AddSupplier`;
      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status !== 'success')
        throw new Error(res.data?.message || 'Save failed');
      notify(isEdit ? 'Supplier updated' : 'Supplier added');
      // Refresh BEFORE closing the dialog so the grid behind it is already
      // populated with the new row when the dialog disappears. Original
      // code fired fetchSuppliers() without await *after* setDialogOpen(false),
      // which let React re-render against stale state until the GET resolved.
      await fetchSuppliers();
      setDialogOpen(false);
    } catch (err) {
      console.error('Save supplier failed:', err);
      notify(err.response?.data?.message || err.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteSupplier = async (supplier) => {
    const ok = window.confirm(
      `Deactivate supplier "${supplier.supplierName}"?\nThey will no longer appear in new purchase entries.`,
    );
    if (!ok) return;
    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/Home/DeleteSupplier/${supplier.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.status !== 'success')
        throw new Error(res.data?.message || 'Delete failed');
      notify('Supplier deactivated');
      fetchSuppliers();
    } catch (err) {
      notify(err.response?.data?.message || err.message || 'Delete failed', 'error');
    }
  };

  const showOutstanding = (supplier) => {
    const out = outstandingMap[supplier.id];
    setOutstandingDetail({ supplier, outstanding: out });
    setOutstandingDialogOpen(true);
  };

  // ─── Initial avatar colour from name hash so each supplier is distinct ─
  const colorFromName = (name) => {
    const palette = ['#1565c0', '#2e7d32', '#ef6c00', '#c62828', '#6a1b9a', '#00838f'];
    if (!name) return palette[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return palette[Math.abs(h) % palette.length];
  };

  // ─── Render ────────────────────────────────────────────────────────────
  if (loading)
    return (
      <Box display='flex' justifyContent='center' mt={10}>
        <CircularProgress />
      </Box>
    );

  return (
    <Container maxWidth='xl' sx={{ mt: 3, mb: 6 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 3,
          justifyContent: 'center',
        }}>
        <LocalShippingIcon sx={{ fontSize: 32, color: '#1565c0' }} />
        <Typography variant='h4' fontWeight='bold'>
          Supplier Master
        </Typography>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant='caption' color='text.secondary'>
                Total Suppliers
              </Typography>
              <Typography variant='h4' fontWeight='bold' color='#1565c0'>
                {suppliers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant='caption' color='text.secondary'>
                Active
              </Typography>
              <Typography variant='h4' fontWeight='bold' color='#2e7d32'>
                {suppliers.filter((s) => s.activeStatus).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant='caption' color='text.secondary'>
                Total Outstanding
              </Typography>
              <Typography variant='h5' fontWeight='bold' color='#ef6c00'>
                {Companycurrency}
                {outstanding
                  .reduce((s, o) => s + Number(o.outstanding || 0), 0)
                  .toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Typography variant='caption' color='text.secondary'>
                Total Purchase Value
              </Typography>
              <Typography variant='h5' fontWeight='bold' color='#6a1b9a'>
                {Companycurrency}
                {outstanding
                  .reduce((s, o) => s + Number(o.totalPurchases || 0), 0)
                  .toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters + Add */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
          <TextField
            size='small'
            placeholder='Search name, mobile, GST, company...'
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
          <TextField
            select
            size='small'
            label='Status'
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 140 }}
            SelectProps={{ native: true }}>
            <option value='all'>All</option>
            <option value='active'>Active only</option>
            <option value='inactive'>Inactive only</option>
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={openAdd}
            size='medium'>
            Add Supplier
          </Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#0a2540' }}>
                {[
                  '#',
                  'Supplier',
                  'Contact',
                  'GST / Company',
                  'Address',
                  'Outstanding',
                  'Status',
                  'Actions',
                ].map((h) => (
                  <TableCell key={h} sx={{ color: '#fff', fontWeight: 700 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align='center' sx={{ py: 6 }}>
                    <Typography color='text.secondary'>
                      No suppliers found. Click "Add Supplier" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((s, i) => {
                  const out = outstandingMap[s.id];
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              bgcolor: colorFromName(s.supplierName),
                              width: 36,
                              height: 36,
                              fontWeight: 700,
                            }}>
                            {(s.supplierName || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600} fontSize={14}>
                              {s.supplierName}
                            </Typography>
                            {s.contactPerson && (
                              <Typography
                                variant='caption'
                                color='text.secondary'>
                                Contact: {s.contactPerson}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>{s.mobileNumber || '—'}</Typography>
                        <Typography
                          variant='caption'
                          color='text.secondary'>
                          {s.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>
                          {s.gstNumber || '—'}
                        </Typography>
                        <Typography
                          variant='caption'
                          color='text.secondary'>
                          {s.companyName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography
                          variant='caption'
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                          {s.address || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {out ? (
                          <Box>
                            <Chip
                              size='small'
                              label={`${Companycurrency}${Number(out.outstanding || 0).toFixed(2)}`}
                              color={
                                Number(out.outstanding || 0) > 0
                                  ? 'warning'
                                  : 'success'
                              }
                              sx={{ fontWeight: 700 }}
                            />
                            <Typography
                              variant='caption'
                              display='block'
                              color='text.secondary'>
                              {out.purchaseCount} purchase
                              {out.purchaseCount === 1 ? '' : 's'}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant='caption' color='text.secondary'>
                            No purchases yet
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={s.activeStatus ? 'Active' : 'Inactive'}
                          color={s.activeStatus ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title='View outstanding'>
                          <IconButton size='small' onClick={() => showOutstanding(s)}>
                            <VisibilityIcon fontSize='small' color='info' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Edit'>
                          <IconButton size='small' onClick={() => openEdit(s)}>
                            <EditIcon fontSize='small' color='primary' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Deactivate'>
                          <IconButton size='small' onClick={() => deleteSupplier(s)}>
                            <DeleteIcon fontSize='small' color='error' />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component='div'
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle sx={{ bgcolor: '#1565c0', color: '#fff', fontWeight: 700 }}>
          {form.id > 0 ? `Edit Supplier — #${form.id}` : 'Add Supplier'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label='Supplier Name *'
                fullWidth
                size='small'
                value={form.supplierName}
                onChange={(e) =>
                  setForm({ ...form, supplierName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label='Company Name'
                fullWidth
                size='small'
                value={form.companyName}
                onChange={(e) =>
                  setForm({ ...form, companyName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label='Contact Person'
                fullWidth
                size='small'
                value={form.contactPerson}
                onChange={(e) =>
                  setForm({ ...form, contactPerson: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label='Mobile Number'
                fullWidth
                size='small'
                value={form.mobileNumber}
                onChange={(e) =>
                  setForm({ ...form, mobileNumber: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label='Email'
                fullWidth
                size='small'
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label='GST Number'
                fullWidth
                size='small'
                value={form.gstNumber}
                onChange={(e) =>
                  setForm({ ...form, gstNumber: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Address'
                fullWidth
                multiline
                rows={2}
                size='small'
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.activeStatus}
                    onChange={(e) =>
                      setForm({ ...form, activeStatus: e.target.checked })
                    }
                  />
                }
                label={form.activeStatus ? 'Active' : 'Inactive'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant='contained' onClick={saveSupplier} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Outstanding detail dialog */}
      <Dialog
        open={outstandingDialogOpen}
        onClose={() => setOutstandingDialogOpen(false)}
        maxWidth='xs'
        fullWidth>
        <DialogTitle sx={{ bgcolor: '#1565c0', color: '#fff', fontWeight: 700 }}>
          {outstandingDetail?.supplier?.supplierName} — Outstanding
        </DialogTitle>
        <DialogContent dividers>
          {outstandingDetail?.outstanding ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Row
                label='Total Purchases'
                value={`${Companycurrency}${Number(outstandingDetail.outstanding.totalPurchases || 0).toFixed(2)}`}
              />
              <Row
                label='Total Paid'
                value={`${Companycurrency}${Number(outstandingDetail.outstanding.totalPaid || 0).toFixed(2)}`}
              />
              <Row
                label='Outstanding'
                value={`${Companycurrency}${Number(outstandingDetail.outstanding.outstanding || 0).toFixed(2)}`}
                highlight
              />
              <Row
                label='Purchase Count'
                value={outstandingDetail.outstanding.purchaseCount}
              />
              <Row
                label='Last Purchase'
                value={
                  outstandingDetail.outstanding.lastPurchaseDate
                    ? new Date(
                        outstandingDetail.outstanding.lastPurchaseDate,
                      ).toLocaleDateString('en-GB')
                    : '—'
                }
              />
            </Box>
          ) : (
            <Typography color='text.secondary'>
              No purchases recorded for this supplier yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutstandingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

function Row({ label, value, highlight }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.5,
        ...(highlight && {
          bgcolor: '#fff3e0',
          px: 1.5,
          borderRadius: 2,
        }),
      }}>
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      <Typography
        variant={highlight ? 'h6' : 'body2'}
        fontWeight={highlight ? 800 : 600}
        color={highlight ? '#ef6c00' : 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
}
